import xrpImage from "../assets/xrp.jpg";
import xlmImage from "../assets/xlm.jpg";
import hbarImage from "../assets/hbar.png";
import usdcImage from "../assets/usdc.jpg";
import algoImage from "../assets/algo.png";

// The ONE map from a compute funding rail to its Cphere asset artwork.
//
// Every surface that shows a rail's identity — the Treasury rail cards, the
// Billing Center tiles, the QR centre mark, the funding request asset step —
// reads this map, so XRP can never resolve to one image here and another
// there. Keys are the canonical `railId` the backend registry emits
// (`funding_destinations.railId`); `asset` + `network` are the structured
// identity a record carries when it has no railId. Display labels are never
// parsed: artwork follows identity, not copy.
export const FUNDING_ASSET_VISUALS = Object.freeze({
  xrp: Object.freeze({ key: "xrp", asset: "XRP", network: "xrpl", label: "XRP", fallback: "XRP", src: xrpImage }),
  xlm: Object.freeze({ key: "xlm", asset: "XLM", network: "stellar", label: "XLM", fallback: "XLM", src: xlmImage }),
  hbar: Object.freeze({ key: "hbar", asset: "HBAR", network: "hedera", label: "Hedera", fallback: "HBAR", src: hbarImage }),
  tron_usdc: Object.freeze({ key: "tron_usdc", asset: "USDC", network: "tron", label: "USDC on Tron", fallback: "USDC", src: usdcImage }),
  algo: Object.freeze({ key: "algo", asset: "ALGO", network: "algorand", label: "Algorand", fallback: "ALGO", src: algoImage }),
});

// Historical / alternate spellings of the same identities. Nothing here is a
// second artwork; each alias points back at one canonical key.
const RAIL_ID_ALIASES = Object.freeze({ usdc: "tron_usdc", usdc_tron: "tron_usdc", "usdc-tron": "tron_usdc", ripple: "xrp", stellar: "xlm", hedera: "hbar", algorand: "algo" });
const NETWORK_ALIASES = Object.freeze({
  xrpl: "xrpl", xrp: "xrpl", "xrp ledger": "xrpl", "xrp-ledger": "xrpl", "xrp_ledger": "xrpl", ripple: "xrpl",
  stellar: "stellar", xlm: "stellar",
  hedera: "hedera", hbar: "hedera",
  tron: "tron", trc20: "tron", "trc-20": "tron", "tron (trc20)": "tron",
  algorand: "algorand", algo: "algorand",
});

function norm(value) {
  return String(value ?? "").trim().toLowerCase();
}

/**
 * Resolve the canonical visual for a rail identity.
 *
 * `identity` is a canonical railId string, or an object carrying any of
 * `railId`, `asset` / `assetCode`, `network`. Resolution order is structured
 * only: railId (or alias) → asset + network → asset alone (an asset offered on
 * exactly one rail, e.g. USDC → USDC on Tron). Returns `null` for an unknown
 * identity so callers render an honest text fallback instead of guessing.
 */
export function resolveFundingAssetVisual(identity) {
  const record = typeof identity === "string" ? { railId: identity } : identity || {};
  const railId = norm(record.railId);
  if (railId) {
    const key = FUNDING_ASSET_VISUALS[railId] ? railId : RAIL_ID_ALIASES[railId];
    if (key && FUNDING_ASSET_VISUALS[key]) return FUNDING_ASSET_VISUALS[key];
  }

  const asset = norm(record.asset || record.assetCode);
  if (!asset) return null;
  const byAsset = Object.values(FUNDING_ASSET_VISUALS).filter((visual) => visual.asset.toLowerCase() === asset);
  if (!byAsset.length) return null;
  const rawNetwork = norm(record.network);
  if (rawNetwork) {
    // A named network must match exactly; USDC on a network we do not serve
    // is not silently drawn as USDC on Tron.
    const network = NETWORK_ALIASES[rawNetwork] || rawNetwork;
    return byAsset.find((visual) => visual.network === network) || null;
  }
  return byAsset.length === 1 ? byAsset[0] : null;
}

/** The short text drawn in the frame when no artwork resolves. */
export function fundingAssetFallbackText(identity) {
  const record = typeof identity === "string" ? { railId: identity } : identity || {};
  const visual = resolveFundingAssetVisual(record);
  const text = visual?.fallback || record.assetCode || record.asset || record.label || record.railId || "?";
  return String(text).trim().slice(0, 4).toUpperCase() || "?";
}

export default resolveFundingAssetVisual;
